import i18n from '../i18n';

const t = i18n.t.bind(i18n);

export function getDate(date) {
  return `${date.getDate()}th ${date.toLocaleString('en-us', {
    month: 'short',
  })} ${date.getFullYear()}, ${date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  })}`;
}

export function getActivityDate(date) {
  const d = new Date(date);
  if (Number.isNaN(d?.getTime?.())) return '';

  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const startOfGiven = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const oneDayMs = 24 * 60 * 60 * 1000;
  const diffMs = startOfToday.getTime() - startOfGiven.getTime();

  const timePart = d.toLocaleString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });

  if (diffMs === 0) {
    return `${t('common.today')}, ${timePart}`;
  }

  if (diffMs === oneDayMs) {
    return `${t('common.yesterday')}, ${timePart}`;
  }

  return `${d.getDate()}th ${d.toLocaleString('en-us', {
    month: 'short',
  })} ${d.getFullYear()}, ${timePart}`;
}

function getStatusDisplayLabel(status) {
  switch (status) {
    case 'ClientSubmit':
      return 'Client Submit';
    case 'ClientInterview':
      return 'Client Interview';
    default:
      return status;
  }
}

function getSubStatusDisplayLabel(status) {
  switch (status) {
    case 'ClientRejected':
      return 'Rejected';
    case 'ClientAccepted':
      return 'Accepted';
    case 'ClientNeedsInfo':
      return 'Needs Info';
    case 'ClientNotified':
      return 'Notified';
    case 'ClientViewed':
      return 'Viewed';
    case 'ClientAcceptedClientInterview':
      return 'Accepted';
    default:
      return status;
  }
}

export function getTimelineTitle(event) {
  const user = `${event?.user?.userProfile?.firstName} ${
    event?.user?.userProfile?.lastName
  } ${
    event?.user?.userProfile?.designation
      ? `(${event?.user?.userProfile?.designation})`
      : ''
  }`;
  switch (event?.type) {
    case 'JobApplicationExternalAdded':
      return `${user} ${t(
        'helpers.getTimeline.JobApplicationExternalAdded'
      )} ${getStatusDisplayLabel(event.jobApplication?.status)}`;
    case 'JobApplicationClientAdded':
      return `${user} ${t(
        'helpers.getTimeline.JobApplicationClientAdded'
      )} ${getStatusDisplayLabel(event.jobApplication?.status)}`;
    case 'JobPostExternalCandidateAdded':
      return `${user} ${t(
        'helpers.getTimeline.JobPostExternalCandidateAdded'
      )} ${getStatusDisplayLabel(event.jobApplication?.status)}`;
    case 'JobPostClientAdded':
      return `${t('helpers.getTimeline.JobPostClientAdded')}`;
    case 'JobApplicationExternalInterviewScheduled':
      return `${event.jobApplication?.user?.userProfile?.fullName} ${t(
        'helpers.getTimeline.JobApplicationExternalInterviewScheduled'
      )} ${event.jobApplication?.user?.userProfile?.fullName}`;

    case 'JobApplicationExternalInterviewUpdated':
      return ` ${event.jobApplication?.user?.userProfile?.fullName} ${t(
        'helpers.getTimeline.JobApplicationExternalInterviewUpdated'
      )}`;
    case 'JobApplicationClientInterviewScheduled':
      return ` ${event.jobApplication?.user?.userProfile?.fullName} ${t(
        'helpers.getTimeline.JobApplicationClientInterviewScheduled'
      )}`;
    case 'JobPostCreated':
      return t('helpers.getTimeline.jobPostCreated');
    case 'JobPostUpdated':
      return 'Job details updated.';
    case 'JobApplicationInterviewCanceled':
      return 'Interview cancelled.';
    case 'JobPostStatusChanged':
      return `${t('helpers.getTimeline.jobPostStatusChanged')} ${
        event?.status
      }.`;
    case 'JobPostViewed':
      return `${event?.user ? user : t('helpers.getTimeline.newJobSeeker')} ${t(
        'helpers.getTimeline.jobPostViewed'
      )}`;
    case 'JobPostApplied':
      return `${user} ${t('helpers.getTimeline.jobPostApplied')}`;
    case 'JobApplicationInvitatioCreated':
      return 'Applicant invited to apply.';
    case 'JobPostExpirationExtended':
      return 'Job expiration updated';

    case 'JobApplicationCreated':
      return t('helpers.getTimeline.jobApplicationCreated');
    case 'JobApplicationUpdated':
      return t('helpers.getTimeline.jobApplicationUpdated');
    case 'JobApplicationStatusChanged': {
      const statusLabel = getStatusDisplayLabel(event?.status);
      const subStatusLabel = getSubStatusDisplayLabel(event?.subStatus);
      if (event?.user?.id === event?.jobApplication?.user?.id) {
        return `${t(
          'helpers.getTimeline.jobApplicationStatusChanged'
        )} ${statusLabel} / ${subStatusLabel}`;
      }
      return event?.user
        ? `${t(
            'helpers.getTimeline.jobApplicationStatusChanged'
          )} ${statusLabel} / ${subStatusLabel} ${t(
            'helpers.getTimeline.by'
          )} ${user}.`
        : `${t(
            'helpers.getTimeline.jobApplicationStatusChanged'
          )} ${statusLabel} / ${subStatusLabel}`;
    }
    case 'JobApplicationInvitationCreated':
      return 'Applicant invited to apply.';
    case 'JobApplicationMessageSent':
      return `${user} ${t('helpers.getTimeline.jobApplicationMessageSent')}`;
    case 'JobApplicationFeedbackSent':
      return t('helpers.getTimeline.jobApplicationFeedbackSent');
    case 'JobApplicationViewed':
      return t('helpers.getTimeline.jobApplicationViewed');
    case 'JobApplicatonInterviewFeedbackAdded':
      return t('helpers.getTimeline.jobApplicatonInterviewFeedbackAdded');
    case 'JobApplicationOfferUpdated':
      return t('helpers.getTimeline.jobApplicationOfferUpdated');
    case 'JobApplicationOfferCreated':
      return t('helpers.getTimeline.jobApplicationOfferCreated');
    case 'JobApplicationOnboardingUpdated':
      return t('helpers.getTimeline.jobApplicationOnboardingUpdated');
    case 'JobApplicationOnboardingCreated':
      return t('helpers.getTimeline.jobApplicationOnboardingCreated');
    case 'JobApplicationJoiningUpdated':
      return t('helpers.getTimeline.jobApplicationJoiningUpdated');
    case 'JobApplicationJoiningCreated':
      return t('helpers.getTimeline.jobApplicationJoiningCreated');
    case 'JobApplicationInterviewScheduled':
      return t('helpers.getTimeline.jobApplicationInterviewScheduled');
    case 'JobApplicationInterviewUpdated':
      return t('helpers.getTimeline.jobApplicationInterviewUpdated');
    case 'JobApplicationMessageAddressed':
      return `${user} ${t(
        'helpers.getTimeline.jobApplicationMessageAddressed'
      )}`;

    default:
      return '';
  }
}

export function getActivityTitle(event) {
  const user = `${event?.user?.userProfile?.firstName} ${event?.user?.userProfile?.lastName}`;
  switch (event?.type) {
    case 'JobApplicationExternalAdded':
      return `${user} ${t(
        'helpers.getTimeline.JobApplicationExternalAdded'
      )} ${getStatusDisplayLabel(event.jobApplication?.status)}`;
    case 'JobApplicationClientAdded':
      return `${user} ${t(
        'helpers.getTimeline.JobApplicationClientAdded'
      )} ${getStatusDisplayLabel(event.jobApplication?.status)}`;
    case 'JobPostExternalCandidateAdded':
      return `${user} ${t(
        'helpers.getTimeline.JobPostExternalCandidateAdded'
      )} ${getStatusDisplayLabel(event.jobApplication?.status)}`;
    case 'JobPostClientAdded':
      return `${t('helpers.getTimeline.JobPostClientAdded')}`;
    case 'JobApplicationExternalInterviewScheduled':
      return `${event.jobApplication?.user?.userProfile?.fullName} ${t(
        'helpers.getTimeline.JobApplicationExternalInterviewScheduled'
      )} ${event.jobApplication?.user?.userProfile?.fullName}`;

    case 'JobApplicationExternalInterviewUpdated':
      return ` ${event.jobApplication?.user?.userProfile?.fullName} ${t(
        'helpers.getTimeline.JobApplicationExternalInterviewUpdated'
      )}`;
    case 'JobApplicationClientInterviewScheduled':
      return ` ${event.jobApplication?.user?.userProfile?.fullName} ${t(
        'helpers.getTimeline.JobApplicationClientInterviewScheduled'
      )}`;
    case 'JobPostCreated':
      return t('helpers.getTimeline.jobPostCreated');
    case 'JobPostUpdated':
      return 'Job details updated.';
    case 'JobApplicationInterviewCanceled':
      return 'Interview cancelled.';
    case 'JobPostStatusChanged':
      return `${t('helpers.getTimeline.jobPostStatusChanged')} ${
        event?.status
      }.`;
    case 'JobPostViewed':
      return `${event?.user ? user : t('helpers.getTimeline.newJobSeeker')} ${t(
        'helpers.getTimeline.jobPostViewed'
      )}`;
    case 'JobPostApplied':
      return `${user} ${t('helpers.getTimeline.jobPostApplied')}`;
    case 'JobApplicationInvitatioCreated':
      return 'Applicant invited to apply.';
    case 'JobPostExpirationExtended':
      return 'Job expiration updated';

    case 'JobApplicationCreated':
      return t('helpers.getTimeline.jobApplicationCreated');
    case 'JobApplicationUpdated':
      return t('helpers.getTimeline.jobApplicationUpdated');
    case 'JobApplicationStatusChanged': {
      const statusLabel = getStatusDisplayLabel(event?.status);
      if (event?.user?.id === event?.jobApplication?.user?.id) {
        return `${t(
          'helpers.getTimeline.jobApplicationStatusChanged'
        )} ${statusLabel} / ${event?.subStatus}`;
      }
      return event?.user
        ? `${t(
            'helpers.getTimeline.jobApplicationStatusChanged'
          )} ${statusLabel} / ${event?.subStatus} ${t(
            'helpers.getTimeline.by'
          )} ${user}.`
        : `${t(
            'helpers.getTimeline.jobApplicationStatusChanged'
          )} ${statusLabel} / ${event?.subStatus}`;
    }
    case 'JobApplicationInvitationCreated':
      return 'Applicant invited to apply.';
    case 'JobApplicationMessageSent':
      return `${user} ${t('helpers.getTimeline.jobApplicationMessageSent')}`;
    case 'JobApplicationFeedbackSent':
      return t('helpers.getTimeline.jobApplicationFeedbackSent');
    case 'JobApplicationViewed':
      return t('helpers.getTimeline.jobApplicationViewed');
    case 'JobApplicatonInterviewFeedbackAdded':
      return t('helpers.getTimeline.jobApplicatonInterviewFeedbackAdded');
    case 'JobApplicationOfferUpdated':
      return t('helpers.getTimeline.jobApplicationOfferUpdated');
    case 'JobApplicationOfferCreated':
      return t('helpers.getTimeline.jobApplicationOfferCreated');
    case 'JobApplicationOnboardingUpdated':
      return t('helpers.getTimeline.jobApplicationOnboardingUpdated');
    case 'JobApplicationOnboardingCreated':
      return t('helpers.getTimeline.jobApplicationOnboardingCreated');
    case 'JobApplicationJoiningUpdated':
      return t('helpers.getTimeline.jobApplicationJoiningUpdated');
    case 'JobApplicationJoiningCreated':
      return t('helpers.getTimeline.jobApplicationJoiningCreated');
    case 'JobApplicationInterviewScheduled':
      return t('helpers.getTimeline.jobApplicationInterviewScheduled');
    case 'JobApplicationInterviewUpdated':
      return t('helpers.getTimeline.jobApplicationInterviewUpdated');
    case 'JobApplicationMessageAddressed':
      return `${user} ${t(
        'helpers.getTimeline.jobApplicationMessageAddressed'
      )}`;

    default:
      return '';
  }
}
